/** App entry: upload a CAD file, mesh it, and drive the viewer controls. */

import { Api, MeshResponse, UploadResponse } from "./api.js";
import { Viewer } from "./viewer.js";
import { ColormapName, COLORMAP_NAMES, toCssGradient } from "./colormap.js";

// In dev the Vite server proxies /api to the backend; in prod set VITE_API_BASE.
const API_BASE = (import.meta as { env?: Record<string, string> }).env?.VITE_API_BASE ?? "";
const api = new Api(API_BASE);

function el<T extends HTMLElement>(id: string): T {
  const n = document.getElementById(id);
  if (!n) throw new Error(`#${id} missing`);
  return n as T;
}

interface State {
  upload: UploadResponse | null;
  mesh: MeshResponse | null;
}
const state: State = { upload: null, mesh: null };

function boot(): void {
  const canvas = el<HTMLCanvasElement>("canvas");
  const viewer = new Viewer(canvas);
  (window as unknown as { __viewer: Viewer }).__viewer = viewer;

  const fileInput = el<HTMLInputElement>("cad-file");
  const controls = el<HTMLElement>("mesh-controls");
  const modeSel = el<HTMLSelectElement>("mode");
  const density = el<HTMLInputElement>("density");
  const densityVal = el<HTMLElement>("density-value");
  const generateBtn = el<HTMLButtonElement>("generate");
  const scalarSel = el<HTMLSelectElement>("scalar");
  const colormapSel = el<HTMLSelectElement>("colormap");
  const edgesToggle = el<HTMLInputElement>("edges");
  const statusEl = el<HTMLElement>("status");
  const qualityEl = el<HTMLElement>("quality");
  const scalarBar = el<HTMLElement>("scalar-bar");
  const scalarBarGrad = el<HTMLElement>("scalar-bar-gradient");
  const scalarBarMin = el<HTMLElement>("scalar-bar-min");
  const scalarBarMax = el<HTMLElement>("scalar-bar-max");
  const fitBtn = el<HTMLButtonElement>("fit");
  const shotBtn = el<HTMLButtonElement>("screenshot");

  for (const c of COLORMAP_NAMES) {
    const o = document.createElement("option");
    o.value = c;
    o.textContent = c;
    colormapSel.appendChild(o);
  }

  const setStatus = (msg: string) => (statusEl.textContent = msg);

  density.addEventListener("input", () => (densityVal.textContent = density.value));

  async function applyColorBy(): Promise<void> {
    if (!state.mesh) return;
    const name = scalarSel.value;
    const colormap = colormapSel.value as ColormapName;
    if (!name) {
      viewer.setColorBy(null, colormap);
      scalarBar.hidden = true;
      return;
    }
    try {
      const arr = await api.array(state.mesh.mesh_id, name);
      viewer.setColorBy(arr, colormap);
      scalarBarGrad.style.background = toCssGradient(colormap);
      scalarBarMin.textContent = arr.range[0].toFixed(3);
      scalarBarMax.textContent = arr.range[1].toFixed(3);
      scalarBar.hidden = false;
    } catch (err) {
      console.error("color-by failed", err);
    }
  }

  function populateScalars(mesh: MeshResponse): void {
    scalarSel.replaceChildren();
    const none = document.createElement("option");
    none.value = "";
    none.textContent = "(none)";
    scalarSel.appendChild(none);
    // Point and cell arrays can share a name (e.g. the quality metrics are
    // exposed as both); list each name once — the backend serves the point
    // array when both exist, which is what colours smoothly.
    const seen = new Set<string>();
    for (const a of [...mesh.point_arrays, ...mesh.cell_arrays]) {
      if (a.name === "Normals" || seen.has(a.name)) continue;
      seen.add(a.name);
      const o = document.createElement("option");
      o.value = a.name;
      o.textContent = a.components > 1 ? `${a.name} (|v|)` : a.name;
      scalarSel.appendChild(o);
    }
    scalarSel.value = mesh.default_color_by ?? "";
  }

  async function generate(): Promise<void> {
    if (!state.upload) return;
    generateBtn.disabled = true;
    const mode = modeSel.value;
    setStatus(`meshing (${mode}, density ${density.value})…`);
    qualityEl.textContent = "";
    try {
      const mesh = await api.mesh(state.upload.id, mode, Number(density.value));
      state.mesh = mesh;
      const geom = await api.geometry(mesh.mesh_id);
      viewer.setGeometry(geom);
      populateScalars(mesh);
      await applyColorBy();
      const q = mesh.quality?.scaled_jacobian;
      qualityEl.textContent = q
        ? `scaled Jacobian — min ${q.min.toFixed(3)}, p05 ${q.p05.toFixed(3)}, ` +
          `mean ${q.mean.toFixed(3)}${q.n_inverted > 0 ? ` · ${q.n_inverted} inverted!` : ""}`
        : "";
      const tets = mesh.n_tets ? ` · ${mesh.n_tets.toLocaleString()} tets` : "";
      setStatus(`${mode} mesh: ${mesh.n_points.toLocaleString()} pts · ${mesh.n_cells.toLocaleString()} cells${tets}`);
    } catch (err) {
      setStatus(`meshing failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      generateBtn.disabled = false;
    }
  }

  fileInput.addEventListener("change", async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    controls.hidden = true;
    state.upload = null;
    state.mesh = null;
    setStatus(`uploading ${file.name}…`);
    try {
      const up = await api.uploadCad(file);
      state.upload = up;
      density.min = String(up.min_density);
      density.max = String(up.max_density);
      density.value = String(up.default_density);
      densityVal.textContent = String(up.default_density);
      controls.hidden = false;
      setStatus(`loaded ${file.name} (${up.n_faces} faces) — generating surface preview…`);
      await generate();
    } catch (err) {
      setStatus(`upload failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  });

  modeSel.addEventListener("change", () => void generate());
  generateBtn.addEventListener("click", () => void generate());
  scalarSel.addEventListener("change", () => void applyColorBy());
  colormapSel.addEventListener("change", () => void applyColorBy());
  edgesToggle.addEventListener("change", () => viewer.setShowEdges(edgesToggle.checked));
  fitBtn.addEventListener("click", () => viewer.fit());
  shotBtn.addEventListener("click", () => {
    const a = document.createElement("a");
    a.href = viewer.screenshot();
    a.download = `${state.mesh?.mesh_id ?? "cad"}.png`;
    a.click();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot, { once: true });
} else {
  boot();
}
